import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatRupiahFull, formatRupiahShort, formatTanggalID, currentBulan, formatBulanID } from "@/lib/format";
import { ChevronLeft, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

export default function Monitor() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulan, setBulan] = useState(currentBulan());
  const [selectedMember, setSelectedMember] = useState(null);
  const [transaksi, setTransaksi] = useState([]);
  const [loadingTrx, setLoadingTrx] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/monitor/transaksi?bulan=${bulan}`);
      setData(res.data);
      // Auto select first member kalau ada
      if (res.data.members.length > 0 && !selectedMember) {
        setSelectedMember(res.data.members[0]);
        loadTransaksi(res.data.members[0].user_id);
      }
    } catch {
      toast.error("Gagal memuat data monitor");
    } finally {
      setLoading(false);
    }
  };

  const loadTransaksi = async (userId) => {
    setLoadingTrx(true);
    try {
      const res = await api.get(`/monitor/transaksi/${userId}?bulan=${bulan}`);
      setTransaksi(res.data);
    } catch {
      toast.error("Gagal memuat transaksi");
    } finally {
      setLoadingTrx(false);
    }
  };

  const hapusTrx = async (trxId) => {
    if (!window.confirm("Hapus transaksi ini?")) return;
    try {
      await api.delete(`/transaksi/${trxId}`);
      toast.success("Transaksi dihapus");
      loadTransaksi(selectedMember.user_id);
      load();
    } catch {
      toast.error("Gagal menghapus transaksi");
    }
  };

  useEffect(() => { load(); }, [bulan]);

  const selectMember = (member) => {
    setSelectedMember(member);
    loadTransaksi(member.user_id);
  };

  if (loading) {
    return (
      <div className="p-6 text-slate-500 text-sm">Memuat data monitor...</div>
    );
  }

  return (
    <div className="pb-4" style={{ background: "#F0F4FF", minHeight: "100vh" }}>

      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <button
          onClick={() => nav("/")}
          className="w-9 h-9 rounded-full bg-white border flex items-center justify-center flex-shrink-0"
          style={{ borderColor: "#E5E7EB" }}
        >
          <ChevronLeft size={18} style={{ color: "#374151" }} />
        </button>
        <div>
          <div className="text-base font-bold" style={{ color: "#111827" }}>Monitor Anggota</div>
          <div className="text-xs" style={{ color: "#6B7280" }}>Pantau pengeluaran anggota keluarga</div>
        </div>
      </div>

      {/* Filter bulan */}
      <div className="px-4 mb-3">
        <input
          type="month"
          value={bulan}
          onChange={(e) => setBulan(e.target.value)}
          className="h-10 px-3 rounded-xl border text-sm bg-white"
          style={{ borderColor: "#E5E7EB" }}
        />
      </div>

      {/* Tidak ada member */}
      {(!data?.members || data.members.length === 0) && (
        <div className="px-4">
          <div className="bg-white rounded-2xl p-8 text-center border" style={{ borderColor: "#E5E7EB" }}>
            <Users size={32} className="mx-auto mb-3" style={{ color: "#9CA3AF" }} />
            <div className="text-sm font-semibold" style={{ color: "#374151" }}>Belum ada anggota</div>
            <div className="text-xs mt-1" style={{ color: "#6B7280" }}>Tambah anggota keluarga dari menu Settings</div>
          </div>
        </div>
      )}

      {/* List member + summary */}
      {data?.members?.map((member) => (
        <div key={member.user_id} className="px-4 mb-3">
          <button
            onClick={() => selectMember(member)}
            className="w-full bg-white rounded-2xl p-4 text-left"
            style={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: selectedMember?.user_id === member.user_id ? "2px solid #2563EB" : "0.5px solid #E5E7EB"
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-bold" style={{ color: "#111827" }}>{member.name}</div>
                <div className="text-xs" style={{ color: "#6B7280" }}>{member.email}</div>
              </div>
              <div className="text-right">
                <div className="text-xs" style={{ color: "#6B7280" }}>Terpakai</div>
                <div className="text-sm font-bold" style={{ color: "#EF4444" }}>{formatRupiahShort(member.total_keluar)}</div>
              </div>
            </div>
            <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background: "#F3F4F6" }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${member.pct_terpakai}%`,
                  background: member.pct_terpakai >= 90 ? "#EF4444" : member.pct_terpakai >= 70 ? "#EA580C" : "#2563EB"
                }}
              />
            </div>
            <div className="flex justify-between text-xs" style={{ color: "#6B7280" }}>
              <span>{member.pct_terpakai}% dari jatah {formatRupiahShort(member.jatah_bulanan)}</span>
              <span>Sisa {formatRupiahShort(member.sisa_jatah)}</span>
            </div>
          </button>
        </div>
      ))}

      {/* Detail transaksi member terpilih */}
      {selectedMember && (
        <div className="px-4">
          <div className="text-sm font-bold mb-2" style={{ color: "#111827" }}>
            Transaksi {selectedMember.name} — {formatBulanID(bulan)}
          </div>
          {loadingTrx ? (
            <div className="text-xs text-slate-400 py-4 text-center">Memuat...</div>
          ) : transaksi.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center text-xs border" style={{ color: "#6B7280", borderColor: "#E5E7EB" }}>
              Belum ada transaksi bulan ini.
            </div>
          ) : (
            <div className="bg-white rounded-2xl overflow-hidden border" style={{ borderColor: "#F3F4F6", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              {transaksi.map((t, i) => (
                <div
                  key={t.id}
                  className={`flex items-center justify-between px-4 py-3 ${i > 0 ? "border-t" : ""}`}
                  style={{ borderColor: "#F3F4F6" }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate" style={{ color: "#111827" }}>{t.deskripsi}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: "#9CA3AF" }}>{formatTanggalID(t.tanggal)}</div>
                  </div>
                  <div className="flex items-center gap-3 ml-2">
                    <div className="text-xs font-bold whitespace-nowrap" style={{ color: "#EF4444" }}>
                      -{formatRupiahFull(t.nominal)}
                    </div>
                    <button
                      onClick={() => hapusTrx(t.id)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "#FEF2F2" }}
                    >
                      <Trash2 size={13} style={{ color: "#EF4444" }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}